import { useMemo } from "react";

import { formatTimestamp } from "~/utils/helpers";
import { urlFor } from "~/utils/url";
import { getArtworkFromData } from "~/utils/data";
import { useLabels } from "~/hooks/useLabels";
import { useUserContext } from "~/hooks/useUserContext";
import { QuickNavigationItemType } from "~/components/quickNavigation/QuickNavigation";
import { RouteType } from "~/constants";

import type { Class } from "~/graphql/types";
import type { QuickNavigationItem } from "~/components/quickNavigation/QuickNavigation";

/**
 * Use to construct the data needed to render the
 * "QuickNavigation" session switcher.
 */
export function useSessionSwitcher(classData: Partial<Class> = {}) {
  const { t } = useLabels();
  const { currentUser, promptLogin } = useUserContext();
  const isClassComplete = Boolean(classData.user?.completedAt);

  const menuItems = useMemo(() => {
    const certificate: QuickNavigationItem | undefined = isClassComplete
      ? {
          id: "certificate",
          type: QuickNavigationItemType.certificate,
          preTitle: t("classCompleted.menu.label"),
          title: t("class.certificateStatus.postTitle.complete"),
          artwork: getArtworkFromData(classData.artwork, "1:1")?.url,
          disabled: !isClassComplete,
          url: urlFor(RouteType.CLASS_COMPLETE, { class: classData.slug! }),
        }
      : undefined;

    return classData.modules?.nodes
      ?.reduce((items, module) => {
        const authenticated = Boolean(currentUser);

        const item: QuickNavigationItem = {
          id: module.id,
          type: QuickNavigationItemType.module,
          preTitle: t("quickNavigation.module.preTitle", {
            number: module.position,
          }),
          title: module.title,
          position: module.position,
          artwork: getArtworkFromData(module.artwork, "1:1")?.url,
          progress: module.user?.progress,
        };

        const sessions: QuickNavigationItem[] =
          module.sessions?.nodes?.map((session) => ({
            id: session.id,
            type: QuickNavigationItemType.session,
            preTitle: t("quickNavigation.session.preTitle", {
              number: session.position,
            }),
            title: session.title,
            postTitle: formatTimestamp(session.duration),
            progress: session.user?.completedAt ? 1 : session.user?.progress,
            position: session.position,
            url: urlFor(RouteType.SESSION, {
              class: module.class?.slug!,
              session: String(session.position),
            }),
          })) ?? [];

        const quiz: QuickNavigationItem | undefined = module.quiz
          ? {
              id: module.quiz.id,
              type: QuickNavigationItemType.quiz,
              preTitle: t("common.quiz"),
              title: module.quiz.title,
              postTitle: t("quickNavigation.quiz.postTitle", {
                number: module.quiz.questions.length,
              }),
              progress: module.quiz.user?.completedAt
                ? 1
                : module.quiz?.user?.progress,
              position: module.quiz.position,
              url: authenticated
                ? urlFor(RouteType.QUIZ, {
                    class: module.class?.slug!,
                    quiz: String(module.quiz.position),
                  })
                : undefined,
              onClick: !authenticated ? promptLogin : undefined,
            }
          : undefined;

        return items.concat(item, sessions, quiz ?? []);
      }, [] as QuickNavigationItem[])
      .concat(certificate ?? []);
  }, [
    classData.artwork,
    classData.modules?.nodes,
    classData.slug,
    currentUser,
    isClassComplete,
    promptLogin,
    t,
  ]);

  return {
    menuItems,
  };
}
